import { useEffect, useRef, useState, type ReactNode } from 'react';
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ArrowDown, ArrowRight, Check, Maximize, Pause, Play, ShieldCheck, SkipForward, Tv, Volume2 } from 'lucide-react';
import type { HomeCopy } from './copy';
import { SignalField } from './SignalField';
import { IPhonePreview } from './IPhonePreview';

type ProductLayout = {
  top: number; width: number;
  /** Chapter 01 screen scale and the pixel lift that parks the screen under the chapter 02/03 heading. */
  scale: number; lift: number;
  phone: { width: number; top: number; right: number; lift: number };
  link: { top: number; right: number; width: number; height: number };
};

/** Text bounds of an element, relative to the stage, ignoring the block box around centred text. */
function textBox(element: Element, stageLeft: number, copyTop: number, copyBoxTop: number) {
  const range = document.createRange();
  range.selectNodeContents(element);
  const box = range.getBoundingClientRect();
  return { right: box.right - stageLeft, bottom: copyTop + (box.bottom - copyBoxTop) };
}

/** One product, three chapters. Native page scroll is the timeline. */
export function HeroJourney({ c, tr, children }: { c: HomeCopy; tr: (key: string) => string; children: ReactNode }) {
  const track = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const intro = useRef<HTMLDivElement>(null);
  const navigation = useRef<HTMLDivElement>(null);
  const cinemaHeading = useRef<HTMLDivElement>(null);
  const connectHeading = useRef<HTMLDivElement>(null);
  const link = useRef<HTMLDivElement>(null);
  const phoneFrame = useRef<HTMLDivElement>(null);
  const [productLayout, setProductLayout] = useState<ProductLayout>();
  const reduced = useReducedMotion();
  const [shortViewport, setShortViewport] = useState(false);
  const [chapter, setChapter] = useState(0);
  const { key: navigationKey, hash } = useLocation();
  const staticMode = Boolean(reduced || shortViewport);
  const { scrollYProgress: progress } = useScroll({ target: track, offset: ['start start', 'end end'] });

  useEffect(() => { if (!hash) setChapter(0); }, [navigationKey, hash]);

  useEffect(() => {
    const query = window.matchMedia('(max-height: 700px), (max-width: 700px) and (max-height: 780px)');
    const update = () => setShortViewport(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // The devices are the hero: size them from the room the stage really has.
  // Chapters 02/03 own the space between their heading and the controls, so the
  // TV is sized for that and lifted there in pixels. In chapter 01 it sits under
  // the intro copy and may rest partly behind the glass deck, like a screen
  // rising into view, instead of shrinking to whatever the copy leaves over.
  useEffect(() => {
    const area = stage.current;
    const copy = intro.current;
    const nav = navigation.current;
    const headings = [cinemaHeading.current, connectHeading.current];
    const label = link.current?.querySelector('span');
    const frame = phoneFrame.current;
    if (!area || !copy || !nav || !headings[0] || !headings[1] || !label || !frame) return;
    const fit = () => {
      const stageWidth = area.clientWidth;
      const compact = stageWidth <= 700;
      const gap = 12;
      const deckTop = nav.offsetTop;
      const copyBottom = copy.offsetTop + copy.offsetHeight;
      const headingBottom = Math.max(...headings.map(heading => heading!.offsetTop + heading!.offsetHeight));
      // Chapters 02/03 may rest 10px of bezel behind the deck; the controls stay clear.
      const room = Math.max(160, deckTop + 10 - headingBottom - gap);
      const width = Math.min(1120, stageWidth * (compact ? .94 : .92), room * (16 / 9) / 1.04);
      const height = width * 9 / 16;
      // Up to 28% of the chapter 01 screen may sit behind the deck; shrink only past that.
      const scale = Math.max(.6, Math.min(.86, (deckTop - copyBottom - gap - 4) / (.72 * height)));
      // Spare room on tall stages is shared above and below the screen instead of piling up under it.
      const top = copyBottom + gap + 4 + Math.max(0, deckTop - 8 - copyBottom - gap - 4 - height * scale) * .4;
      const lift = headingBottom + gap + Math.max(0, deckTop + 10 - headingBottom - gap - height * 1.04) * .4 - top;

      // The phone stands on the deck beside the screen (or rides the screen when the deck is far
      // below), never over the intro copy: a copy line it would overlap horizontally pushes it down.
      const productLeft = (stageWidth - width) / 2;
      const phoneWidth = Math.round(width * (compact ? .28 : .30));
      const phoneHeight = phoneWidth * (frame.offsetHeight / frame.offsetWidth || 2.11);
      const phoneRight = Math.round(Math.min(width * .22, Math.max(0, productLeft - 8 - phoneHeight * .062)));
      const phoneLeft = productLeft + width + phoneRight - phoneWidth;
      const stageLeft = area.getBoundingClientRect().left;
      const copyBoxTop = copy.getBoundingClientRect().top;
      let phoneTop = Math.min(deckTop - 8 - phoneHeight, top + height * scale * .44 - phoneHeight / 2);
      copy.querySelectorAll('h1, p, a, button').forEach(line => {
        const box = textBox(line, stageLeft, copy.offsetTop, copyBoxTop);
        if (phoneLeft < box.right + 12 && phoneTop < box.bottom + 8) phoneTop = box.bottom + 8;
      });
      // Chapter 03 brings the phone forward: clear of the deck, but never into the heading.
      const phoneLift = Math.max(headingBottom + gap - phoneTop, Math.min(-8, deckTop - 6 - phoneTop - phoneHeight * 1.02));
      const phone = { width: phoneWidth, top: Math.round(phoneTop - top), right: -phoneRight, lift: Math.round(phoneLift) };
      // The "sent" link: a label on the screen's top-right corner (chapter 03 parks the screen at
      // 88% and 10% to the left), a line towards the phone and a drop that hides behind it.
      const phoneEdge = width + phoneRight - phoneWidth;
      const labelLeft = Math.min(phoneEdge - 22 - label.offsetWidth, width * .84 - label.offsetWidth + 10);
      const linkRight = width - (phoneEdge + 14);
      const linkBox = { top: Math.round(lift - (compact ? 4 : 10)), right: Math.round(linkRight), width: Math.round(width - linkRight - labelLeft - 8), height: Math.round(height * .5) };
      const next = { top, width, scale, lift, phone, link: linkBox };

      setProductLayout(previous => previous && JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    };
    const observer = new ResizeObserver(fit);
    [area, copy, nav, label, frame, ...headings].forEach(element => observer.observe(element!));
    fit();
    return () => observer.disconnect();
  }, [staticMode]);

  const restScale = productLayout?.scale ?? .8;
  const lift = productLayout?.lift ?? 0;
  const phoneLift = productLayout?.phone.lift ?? -8;
  const phoneBox = productLayout && { width: productLayout.phone.width, top: productLayout.phone.top, right: productLayout.phone.right };

  useMotionValueEvent(progress, 'change', value => {
    if (!staticMode) setChapter(value < .3 ? 0 : value < .69 ? 1 : 2);
  });

  const introOpacity = useTransform(progress, [0, .13, .27], [1, 1, 0]);
  const introY = useTransform(progress, [0, .27], [0, -45]);
  const cinemaOpacity = useTransform(progress, [.23, .33, .6, .69], [0, 1, 1, 0]);
  const connectOpacity = useTransform(progress, [.64, .75], [0, 1]);
  const screenScale = useTransform(progress, [0, .35, .62, .88], [restScale, 1.04, 1.04, .88]);
  const screenY = useTransform(progress, [0, .35], [0, lift]);
  const screenX = useTransform(progress, [.64, .9], ['0%', '-10%']);
  const rotateY = useTransform(progress, [0, .35, .64, .9], [-9, 0, 0, -10]);
  const rotateX = useTransform(progress, [0, .35], [8, 0]);
  const playbackOpacity = useTransform(progress, [.22, .37], [0, 1]);
  const footageScale = useTransform(progress, [.3, 1], [1.15, 1]);
  const phoneY = useTransform(progress, [0, .35, .65, .9], [0, phoneLift + 30, phoneLift + 30, phoneLift]);
  const phoneRotate = useTransform(progress, [0, .35, .65, .9], [7, 11, 11, -5]);
  const phoneOpacity = useTransform(progress, [0, .27, .6, .8], [1, .38, .38, 1]);
  const phoneScale = useTransform(progress, [0, .35, .65, .9], [1, .85, .85, 1.04]);

  function goToChapter(index: number) {
    if (staticMode) { setChapter(index); return; }
    if (!track.current) return;
    const start = track.current.getBoundingClientRect().top + window.scrollY;
    const travel = track.current.offsetHeight - window.innerHeight;
    window.scrollTo({ top: start + travel * [0, .46, .88][index], behavior: 'smooth' });
  }

  return <div ref={track} className={`moova-journey${staticMode ? ' is-static' : ''}`} data-chapter={chapter}>
    <div ref={stage} className="moova-journey-sticky">
      <SignalField progress={progress} reduced={staticMode} focusY={productLayout ? productLayout.top + productLayout.width * .2 : undefined} productWidth={productLayout?.width} />

      <motion.div ref={intro} className="moova-hero-copy moova-container" aria-hidden={chapter !== 0} style={staticMode ? { opacity: chapter === 0 ? 1 : 0 } : { opacity: introOpacity, y: introY }}>
        <span className="moova-eyebrow"><i aria-hidden="true" />{c.eyebrow}</span>
        <h1 id="moova-title"><span className="moova-title-intro">{c.headline}</span><span className="moova-title-dimension">{c.headline2}</span></h1>
        <p className="moova-hero-lead">{c.lead}</p>
        <div className="moova-hero-actions"><a className="moova-button" href="#setup" tabIndex={chapter === 0 ? 0 : -1}>{tr('heroCta')}<ArrowRight size={16} /></a><button className="moova-text-link" onClick={() => goToChapter(1)} tabIndex={chapter === 0 ? 0 : -1}>{c.explore}<ArrowDown size={16} /></button></div>
        <p className="moova-trial"><ShieldCheck size={12} aria-hidden="true" />{c.trial}</p>
      </motion.div>

      <motion.div ref={cinemaHeading} className="moova-journey-heading" aria-hidden={chapter !== 1} style={staticMode ? { opacity: chapter === 1 ? 1 : 0 } : { opacity: cinemaOpacity }}>
        <span className="moova-eyebrow">02 — {c.resume}</span>
        <h2>{c.cinemaTitle}<br /><span>{c.cinemaTitle2}</span></h2>
        <p>{c.resumeText}</p>
      </motion.div>
      <motion.div ref={connectHeading} className="moova-journey-heading" aria-hidden={chapter !== 2} style={staticMode ? { opacity: chapter === 2 ? 1 : 0 } : { opacity: connectOpacity }}>
        <span className="moova-eyebrow">03 — {c.control}</span>
        <h2>{c.phoneTitle}<br /><span>{c.phoneTitle2}</span></h2>
        <p>{c.phoneLead}</p>
      </motion.div>

      <div className="moova-journey-product" aria-hidden="true" style={productLayout && { top: productLayout.top, width: productLayout.width }}>
        <motion.div className="moova-journey-tv" style={staticMode ? { scale: chapter === 0 ? restScale : chapter === 1 ? 1.04 : .88, y: chapter === 0 ? 0 : lift, x: chapter === 2 ? '-10%' : '0%' } : { scale: screenScale, y: screenY, x: screenX, rotateX, rotateY }}>
          <div className="moova-screen-aura" />
          {children}
          <motion.div className="moova-journey-playback" style={{ opacity: staticMode ? Number(chapter > 0) : playbackOpacity }}>
            <motion.img className="moova-sintel-scene" src="/assets/home/mobile/player.webp" alt="" style={staticMode ? undefined : { scale: footageScale }} />
            <div className="moova-playback-shade" />
            <div className="moova-playback-brand"><img src="/assets/moova-logo.png" alt="" /><span>{c.resume}</span></div>
            <div className="moova-playback-info"><span>BLENDER OPEN MOVIE</span><h3>Sintel</h3><p>{c.resumeTitle}</p></div>
            <div className="moova-playback-controls"><div className="moova-playback-timeline"><i style={{ transform: `scaleX(${25 / 888})` }} /></div><div><Pause fill="currentColor" /><SkipForward /><Volume2 /><span>0:25</span><span className="moova-playback-duration">/ 14:48</span><Maximize /></div></div>
          </motion.div>
        </motion.div>

        <motion.div ref={link} className="moova-journey-connection" style={{ ...productLayout?.link, opacity: staticMode ? Number(chapter === 2) : connectOpacity }}><i /><i /><i /><span><Check size={12} />{c.sent}</span></motion.div>
        <motion.div ref={phoneFrame} className="moova-journey-phone" style={staticMode ? { ...phoneBox, opacity: chapter === 1 ? .38 : 1, rotate: chapter === 2 ? -5 : 7, y: chapter === 0 ? 0 : chapter === 1 ? phoneLift + 30 : phoneLift } : { ...phoneBox, opacity: phoneOpacity, y: phoneY, rotate: phoneRotate, scale: phoneScale }}>
          <IPhonePreview screen={chapter === 0 ? 'home' : 'player'} c={c} eager />
        </motion.div>
      </div>

      <div ref={navigation} className="moova-journey-nav">
        <div className="moova-journey-progress" aria-hidden="true"><motion.i style={{ scaleX: staticMode ? (chapter + 1) / 3 : progress }} /></div>
        <div className="moova-journey-chapters" role="group" aria-label={c.explore}>{[c.browse, c.resume, c.control].map((label, index) => <button key={label} onClick={() => goToChapter(index)} aria-pressed={chapter === index}><span>0{index + 1}</span>{index === 0 ? <Tv size={14} /> : index === 1 ? <Play size={14} /> : <Volume2 size={14} />}{label}</button>)}</div>
        <span className="moova-journey-scroll">{c.scrollExplore}<ArrowDown size={12} /></span>
      </div>
    </div>
  </div>;
}
